// Calculator type definitions

export interface CalculationRequest {
  useCase: string;
  gpuType: string;
  enableRAG: boolean;
  highAvailability: boolean; 
  enableNvlink: boolean;
  modelFamily: string;
  precision: string;
  concurrentUsers: number;
  requestsPerUserPerMinute?: number;
  inputTokens: number;
  outputTokens: number;
  gpuOverhead: number;
  gpuCount?: number | null;
  customModelName: string;
  params?: number;
  layers?: number;
  hidden_size?: number;
  kv_heads?: number;
  heads?: number;
  is_moe? : boolean;
  active_params?: number;
}

export interface GPUConfiguration {
  name: string;
  quantity: number;
  vramPerGpu: number;
  totalVram: number;
  description: string;
}

export interface TimeValue {
  value: number;
  unit: string;
}

export interface PerformanceMetrics {
  prefill_time: number | TimeValue;
  generation_time: number | TimeValue;
  estimated_response_time: number | TimeValue;
  max_conc_users: string;
  activeRequests?: number; // requests in flight the latency figures are for
  typicalErrorPct?: { ttft?: number; itl?: number };
  lowConfidence?: boolean; // e.g. MoE models
}

export interface MemoryRequirements {
  model: number;
  rag: number;
  total: number;
}

export interface CPURequirements {
  cores: number; // total across GPU servers
  ram: number; // GB, total across GPU servers
  storage: string;
  coresPerNode?: number;
  ramPerNode?: number;
  nodes?: number;
}

export interface ServerConfiguration {
  compute: string;
  storage: string;
  network: string;
  networking: string;
  category: string;
  // Recommended Lenovo Hybrid AI platform for a deployment of this size.
  platform?: string | null;
  platform_spec?: string | null;
  platform_servers?: string | null;
  platform_best_for?: string | null;
  platform_rationale?: string | null;
  platform_url?: string | null;
  platform_tier?: string | null; // AI Micro-Factory / AI Factory / AI Super-Factory
  nodes?: number;
  gpusPerNode?: number;
  serviceNodes?: number;
  rsus?: number; // Rack Scalable Units (4 compute nodes each)
  switches?: number;
  power?: PowerEstimate | null;
}

export interface PowerItem {
  label: string;
  count: number;
  model: string;
  idle_w: number;
  peak_w: number;
}

export interface PowerEstimate {
  items: PowerItem[];
  idle_kw: number;
  peak_kw: number;
  notes: string[];
}

// Pre-quote validation checklist (Lenovo Enterprise AI sizing rubric).
export interface Check {
  level: "warning" | "info";
  text: string;
}

// Cluster-wide GB. used = weights + kvCache + activations + rag, and
// used + overhead + free == total (free is negative when the workload doesn't fit).
export interface VRAMBreakdown {
  total: number;
  used: number;
  free: number;
  overhead: number;
  weights: number;
  kvCache: number;
  activations: number;
  rag: number;
  gpuCount: number;
  vramPerGpu: number;
  paramsB: number;
  bytesPerParam: number;
  users: number;
  sequenceTokens: number;
  kvPerUser: number;
  copies: number; // model copies, each with its own weights + activation buffer
  activationPerCopy: number;
  activationBatchTokens: number;
  extraUsers: number;
}

export interface CalculationResult {
  inputSummary: CalculationRequest;
  recommendedConfig: GPUConfiguration;
  memoryRequirements: MemoryRequirements;
  cpuRequirements: CPURequirements;
  serverConfiguration: ServerConfiguration;
  otherParameters: PerformanceMetrics;
  vramBreakdown: VRAMBreakdown;
  tensorParallelism?: number;
  pipelineParallelism?: number;
  warning?: string | null;
  checks?: Check[];
  exportRow?: Record<string, string | number>;
}

export const USE_CASES = {
  "chatbot": "Chatbot",
  "multimodal": "MultiModal QA/ Image Analytics",
  "codeassistant": "Code Assistant",
  "content": "Content Generation",
  "documentqa": "Document Analysis/ RAG",
  "api": "API Response Generation",
  "research":"Research Assistant",
  "custom": "Custom Configuration"
} as const;

export const MODEL_FAMILIES = {
  "custom": "Custom Model",
  "llama3-8b": "meta-llama/Meta-Llama-3-8B-Instruct",
  "llama3.1-8b": "meta-llama/Llama-3.1-8B-Instruct",
  "llama3-70b": "meta-llama/Llama-3.3-70B-Instruct",
  "qwen2.5-7b": "Qwen/Qwen2.5-7B-Instruct",
  "qwen2.5-72b": "Qwen/Qwen2.5-72B-Instruct",
  "qwen3-8b": "Qwen/Qwen3-8B",
  "qwen3-32b": "Qwen/Qwen3-32B",
  "qwen3-235b-a22b": "Qwen/Qwen3-235B-A22B",
  "gemma-7b": "google/gemma-7b",
  "gemma2-9b": "google/gemma-2-9b-it",
  "gemma2-27b": "google/gemma-2-27b-it",
  "phi-3-mini-4k": "microsoft/Phi-3-mini-4k-instruct",
  "phi-4": "microsoft/phi-4",
  "mistral-small-24b": "mistralai/Mistral-Small-24B-Instruct-2501",
  "mixtral-8x7b": "mistralai/Mixtral-8x7B-Instruct-v0.1",
  "gpt-oss-20b": "openai/gpt-oss-20b",
  "gpt-oss-120b": "openai/gpt-oss-120b",
  "deepseek-v3": "deepseek-ai/DeepSeek-V3",
  "deepseek-coder-33b": "deepseek-ai/deepseek-coder-33b-instruct",
  "deepseek-moe-16b": "deepseek-ai/deepseek-moe-16b-chat",
  "internvl3-78b": "OpenGVLab/InternVL3-78B",
  "qwen2.5-vl-72b": "Qwen/Qwen2.5-VL-72B-Instruct",
  "qwen3-coder-30b-a3b": "Qwen/Qwen3-Coder-30B-A3B-Instruct",
  "llama4-scout": "meta-llama/Llama-4-Scout-17B-16E-Instruct",
  "gemma4-31b": "google/gemma-4-31B-it",
  "gemma4-26b-a4b": "google/gemma-4-26B-A4B-it",
  "deepseek-r1-distill-llama-70b": "deepseek-ai/DeepSeek-R1-Distill-Llama-70B",
  "deepseek-v3.2": "deepseek-ai/DeepSeek-V3.2"
} as const;

export const PRECISION_OPTIONS = {
  "FP32": "FP32 (32-bit)",
  "FP16": "FP16 (16-bit)",
  "BF16": "BF16 (16-bit)",
  "FP8": "FP8 (8-bit)",
  "INT8": "INT8 (8-bit)",
  "INT4/NF4": "INT4/NF4 (4-bit)"
} as const;

export const PRIORITY_OPTIONS = {
  "throughput": "Throughput",
  "latency": "Latency"
} as const;